import { Injectable } from '@angular/core';

import { Redactor, RedactorApp, RedactorComponentType, RedactorConfig } from '../../redactor.types';
import { Wrapper } from '../interfaces';
import { mergeRedactorConfigArrField, RedactorExtension } from '../redactor-extension';

declare let $R: Redactor;

// plugin tokens
export const QUOTE_PLUGIN_TOKEN = 'rt-quote';
export const QUOTE_COMPONENT_TOKEN = `${QUOTE_PLUGIN_TOKEN}.component`;

// trigger block class name
export const QUOTE_TRIGGER_CLASS = 'redactor-quote-toggle';

// quote block class names
export const QUOTE_DEFAULT_CLASS = 'recruitee-quote';
export const QUOTE_HIDDEN_CLASS = 'redactor-quote--hidden';
export const QUOTE_SHOWN_CLASS = 'redactor-quote--shown';

// redactor stuff

// eslint-disable-next-line max-lines-per-function
const installRtQuoteExtension = () => {
  // TODO: expand on 'select all' from context menu or drag & select

  $R.add('plugin', QUOTE_PLUGIN_TOKEN, {
    init(app: RedactorApp): void {
      this.app = app;
      this.utils = app.utils;
      this.selection = app.selection;
      this.component = app.component;
      this.container = app.container;
      this.caret = app.caret;
      this.editor = app.editor;
      this.insertion = app.insertion;
      this.inspector = app.inspector;
      this.cleaner = app.cleaner;
    },
    start(): void {
      this.cleaner.addConvertRules(QUOTE_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
        const $quotes = $wrapper.find(`.${QUOTE_DEFAULT_CLASS}:not(.${QUOTE_SHOWN_CLASS})`);
        $quotes.before(() => this.component.create(QUOTE_PLUGIN_TOKEN));
        return $wrapper;
      });

      this.cleaner.addUnconvertRules(QUOTE_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
        $wrapper.find(`[data-redactor-type="${QUOTE_PLUGIN_TOKEN}"]`).remove();
        return $wrapper;
      });
    },
    stop(): void {
      delete this.cleaner.convertRules[QUOTE_PLUGIN_TOKEN];
      delete this.cleaner.unconvertRules[QUOTE_PLUGIN_TOKEN];
    },
    onclick(e: MouseEvent): void {
      const activeTrigger = this._getActiveTrigger();

      // direct click
      if (activeTrigger) {
        activeTrigger.expandQuote();
      }
    },
    onkeydown(e: KeyboardEvent): void {
      const activeTrigger = this._getActiveTrigger();

      if (this._isSelectAllEvent(e)) {
        this._expandOnKeyboardSelectAll(e);
      } else if (activeTrigger) {
        activeTrigger.trapKeyboard();
      }
    },
    // cmd/ctrl + a
    _isSelectAllEvent(e: KeyboardEvent): boolean {
      const { metaKey, ctrlKey, keyCode } = e;
      return (ctrlKey || metaKey) && [65, 97].includes(keyCode);
    },
    _getActiveTrigger(): boolean | RedactorComponentType {
      const activeCmp = this.component.getActive();
      return activeCmp
        ? this.inspector.parse(activeCmp).isComponentType(QUOTE_PLUGIN_TOKEN)
          ? this.component.build(activeCmp)
          : false
        : false;
    },
    _getTriggers(): RedactorComponentType[] {
      return this.editor
        .getElement()
        .find(`[data-redactor-type="${QUOTE_PLUGIN_TOKEN}"]`)
        .nodes.map((n: any) => this.component.build(n));
    },
    _expandOnKeyboardSelectAll(): void {
      this._getTriggers().forEach((trigger: RedactorComponentType) => trigger.expandQuote());
      this.selection.setAll();
    },
  });

  $R.add('class', QUOTE_COMPONENT_TOKEN, {
    mixins: ['dom', 'component'],
    init(app: RedactorApp, $trigger: RedactorComponentType): void {
      this.component = app.component;
      this.selection = app.selection;
      this.caret = app.caret;
      this.insertion = app.insertion;

      this._createComponent($trigger);

      return this;
    },
    trapKeyboard(): void {
      this.on(
        'keydown',
        (e: KeyboardEvent) => {
          switch (e.keyCode) {
            case this.selection.keycodes.UP:
              break;

            case this.selection.keycodes.DELETE:
            case this.selection.keycodes.BACKSPACE:
              e.preventDefault();
              e.stopPropagation();
              this._moveCaretOutOfTrigger();
              this.removeQuote();
              break;

            case this.selection.keycodes.ENTER:
              e.preventDefault();
              e.stopPropagation();
              this._moveCaretOutOfTrigger();
              this.expandQuote();
              break;

            case this.selection.keycodes.RIGHT:
            case this.selection.keycodes.DOWN:
              e.preventDefault();
              e.stopPropagation();
              this.expandQuote();
              break;

            default:
              this._moveCaretOutOfTrigger();
              break;
          }
        },
        true,
      );
    },
    expandQuote(removeSelf: boolean = true): void {
      const $quote = this.nextElement();
      $quote.toggleClass(QUOTE_HIDDEN_CLASS);
      $quote.toggleClass(QUOTE_SHOWN_CLASS);

      if (removeSelf) {
        this.component.remove(this);
      }
    },
    removeQuote(removeSelf: boolean = true): void {
      const $quote = this.nextElement();
      $quote.remove();

      if (removeSelf) {
        this.component.remove(this);
      }
    },
    _createComponent($existing: RedactorComponentType): void {
      if ($existing) {
        this.parse($existing);
      } else {
        this.parse(`<div class="${QUOTE_TRIGGER_CLASS}"></div>`);
        this.addClass('redactor-component');
        this.addClass(QUOTE_TRIGGER_CLASS);

        this.attr({
          'data-redactor-type': QUOTE_PLUGIN_TOKEN,
          contenteditable: false,
          tabindex: '-1',
        });
      }
    },
    _moveCaretOutOfTrigger(insertBreakLine: boolean = true): void {
      this.caret.setEnd(this.prevElement() || this.parent());

      if (insertBreakLine) {
        this.insertion.insertBreakLine();
      }
    },
  });
};

@Injectable({
  providedIn: 'root',
})
export class RedactorQuoteExtension extends RedactorExtension {
  constructor() {
    super();

    if (!$R.classes[QUOTE_COMPONENT_TOKEN]) {
      installRtQuoteExtension();
    }
  }

  public canInstall(config: RedactorConfig): boolean {
    return Array.isArray(config.plugins) && config.plugins.includes(QUOTE_PLUGIN_TOKEN);
  }

  public install(config: RedactorConfig): RedactorConfig {
    this.enable();

    const pasteKeepSelector = `div.${QUOTE_DEFAULT_CLASS}`;

    return {
      ...config,
      pasteKeepStyle: mergeRedactorConfigArrField(config, 'pasteKeepStyle', ['blockquote']),
      pasteBlockTags: mergeRedactorConfigArrField(config, 'pasteBlockTags', ['blockquote', 'div']),
      pasteKeepClass: mergeRedactorConfigArrField(config, 'pasteKeepClass', [pasteKeepSelector]),
      pasteKeepAttrs: mergeRedactorConfigArrField(config, 'pasteKeepAttrs', [pasteKeepSelector]),
    };
  }
}
